import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { APP_BUILD_META } from '../../../environments/build-meta.generated';
import { environment } from '../../../environments/environment';

const STORAGE_KEY = 'givo_app_build_id';
const VERSION_URL = '/version.json';
/** Intervalo para detectar novo deploy com a aba aberta (produção/homolog). */
const POLL_MS = 10 * 60 * 1000;

type RemoteBuildMeta = {
  buildId?: string;
  builtAt?: string;
  gitSha?: string;
};

@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private readonly platformId = inject(PLATFORM_ID);
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  /** Deve rodar uma vez na inicialização da aplicação (APP_INITIALIZER). */
  init(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!environment.production) return;

    const bundledId = APP_BUILD_META.buildId;
    const storedId = localStorage.getItem(STORAGE_KEY);

    if (storedId && storedId !== bundledId) {
      localStorage.setItem(STORAGE_KEY, bundledId);
      this.reload();
      return;
    }

    localStorage.setItem(STORAGE_KEY, bundledId);
    void this.syncWithServer();

    this.pollTimer = setInterval(() => void this.syncWithServer(), POLL_MS);
  }

  private async syncWithServer(): Promise<void> {
    try {
      const res = await fetch(`${VERSION_URL}?_=${Date.now()}`, {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;

      const remote = (await res.json()) as RemoteBuildMeta;
      const remoteId = remote.buildId?.trim();
      if (!remoteId || remoteId === APP_BUILD_META.buildId) return;

      localStorage.setItem(STORAGE_KEY, remoteId);
      this.reload();
    } catch {
      /* rede indisponível — ignora */
    }
  }

  private reload(): void {
    globalThis.location.reload();
  }
}

export function initAppVersionFactory(): () => void {
  const service = inject(AppVersionService);
  return () => service.init();
}
